import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Toolbar,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { useAuth } from '../hooks/useAuth';
import { colors } from '../styles/theme';

interface MainLayoutProps {
  variant?: 'default' | 'dashboard' | 'minimal';
  showSidebar?: boolean;
  showFooter?: boolean;
  containerMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  variant = 'default',
  showSidebar = true,
  showFooter = true,
  containerMaxWidth = 'lg',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const drawerWidth = sidebarOpen && !isMobile ? 240 : 0;

  // Determine if sidebar should be shown based on user role and props
  const shouldShowSidebar = showSidebar && user && variant !== 'minimal';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.beige,
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Header */}
      <Header
        onMenuClick={handleDrawerToggle}
        showMenuButton={shouldShowSidebar}
        variant={variant}
      />

      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* Sidebar */}
        {shouldShowSidebar && (
          <Sidebar
            open={sidebarOpen}
            mobileOpen={mobileOpen}
            onClose={() => setMobileOpen(false)}
            width={240}
            variant={variant}
            userRole={user?.role}
          />
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            transition: theme.transitions.create(['margin-left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: !isMobile && sidebarOpen ? `240px` : 0,
            width: !isMobile && sidebarOpen ? `calc(100% - 240px)` : '100%',
          }}
        >
          <Toolbar />
          {containerMaxWidth ? (
            <Container maxWidth={containerMaxWidth} sx={{ py: 2, pl: 2, pr: 3 }}>
              <Outlet />
            </Container>
          ) : (
            <Box sx={{ p: 2, pr: 3 }}>
              <Outlet />
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      {showFooter && variant !== 'minimal' && (
        <Footer
          sx={{
            transition: theme.transitions.create(['margin-left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: !isMobile && sidebarOpen ? `240px` : 0,
            width: !isMobile && sidebarOpen ? `calc(100% - 240px)` : '100%',
          }}
        />
      )}
    </Box>
  );
};

// Layout variants for different sections
export const DashboardLayout: React.FC = () => (
  <MainLayout variant="dashboard" showSidebar={true} showFooter={false} containerMaxWidth={false} />
);

export const MinimalLayout: React.FC = () => (
  <MainLayout variant="minimal" showSidebar={false} showFooter={false} />
);

export const PublicLayout: React.FC = () => (
  <MainLayout variant="default" showSidebar={false} showFooter={true} />
);

export default MainLayout;